/**
 * MobileUpdateModal — in-app update prompt for Android and iOS.
 *
 * On app startup the hook below calls `checkForMobileUpdate()`.
 * If a newer version exists the modal opens.
 *
 * - Android: tapping "Update" downloads the APK and opens the system
 *   package installer via `@capacitor/browser`.
 * - iOS: tapping "Update" opens the App Store URL in Safari.
 */
import { useEffect, useState } from "react";
import Modal, { Styles } from "react-modal";
import { HiOutlineDownload } from "react-icons/hi";
import { isMobile, platform } from "@platform";
import {
  checkForMobileUpdate,
  installMobileUpdate,
  MobileUpdateInfo,
} from "../../../../platform/mobile/src/update/MobileUpdateService";
import "./mobile-update-modal.css";

Modal.setAppElement("#marketview");

const MobileUpdateModal = () => {
  const [updateInfo, setUpdateInfo] = useState<MobileUpdateInfo | null>(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (!isMobile) return;
    // Check on app open — 1 s delay lets Capacitor plugins finish initialising
    const timeout = setTimeout(async () => {
      const info = await checkForMobileUpdate();
      if (info) setUpdateInfo(info);
    }, 1000);
    return () => clearTimeout(timeout);
  }, []);

  if (!isMobile || !updateInfo) return null;

  const handleUpdate = async () => {
    setInstalling(true);
    await installMobileUpdate(updateInfo);
    // On Android the system installer takes over; on iOS Safari opens.
    // We leave the modal open until the OS dismisses the app.
    setInstalling(false);
  };

  const handleCancel = () => {
    setUpdateInfo(null);
    // Quit the app — update is mandatory
    // platform.closeWindow() is a no-op on mobile, so call Capacitor directly
    (window as any).Capacitor?.Plugins?.App?.exitApp?.();
  };

  const overlayStyle: Styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.75)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      position: "relative",
      inset: "auto",
      border: "none",
      background: "transparent",
      overflow: "visible",
      borderRadius: "16px",
      outline: "none",
      padding: 0,
      maxWidth: "380px",
      width: "90%",
    },
  };

  return (
    <Modal isOpen={!!updateInfo} style={overlayStyle} shouldCloseOnOverlayClick={false} shouldCloseOnEsc={false}>
      <div className="mobile-update-wrapper">
        <div className="mobile-update-icon-container">
          <HiOutlineDownload size={36} />
        </div>
        <h3 className="mobile-update-title">Update Available</h3>
        <p className="mobile-update-message">
          A new version of MarketView is available. Install now to get the
          latest improvements.
        </p>
        {updateInfo.platform === "ios" && (
          <p className="mobile-update-hint">
            You will be redirected to the App Store to complete the update.
          </p>
        )}
        <div className="mobile-update-btn-container">
          <button
            type="button"
            className="mobile-update-cancel-btn"
            onClick={handleCancel}
          >
            Later
          </button>
          <button
            type="button"
            className="mobile-update-confirm-btn"
            onClick={handleUpdate}
            disabled={installing}
          >
            {installing ? "Opening…" : "Update Now"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MobileUpdateModal;
